import { HandlerEvent } from 'post-robot';
import { error, ValidationError } from 'common/errors';
import {
  pushCompleteIdentityValidationRequest,
  pushCompleteIdentityValidationResponse,
  pushErrors
} from 'common/gamp';
import { saveUser, UserProfile } from 'communicator/store';
/* START GENAI@GHCOPILOT */
import {
  getAccessToken,
  getApiKey,
  getIdValidationSessionId,
  getIsFeatureToggleEnabled,
  getIsVdiDisabled
} from 'communicator/store/selectors';
/* END GENAI@GHCOPILOT */
import { fetchOrchProbe } from 'communicator/utils/api/probe';
import { getUsernameFromRememberMe } from 'communicator/utils/cookies';
import { getHeader } from 'communicator/utils/fetch';
import { getOrchestratorLockoutFlow } from 'communicator/utils/get-lockout-flow';
import { getOrchestratorOTPErrors } from 'communicator/utils/get-otp-error';
import { markStart, markEnd } from 'communicator/utils/instrumentation';
import parseExpiryFromAccessToken from 'communicator/utils/parse-expiry-from-token';
import unbind from 'communicator/utils/unbind';
import {
  CompleteIdentityValidationData,
  CompleteIdentityValidationPayload
} from 'types/visa-sdk/complete-identity-validation';
import completeIdentityValidationLegacy from '../complete-identity-validation/complete-identity-validation-legacy';

export default async function completeIdentityValidation(
  event: HandlerEvent<CompleteIdentityValidationData>
): Promise<CompleteIdentityValidationPayload | ValidationError> {
  try {
    if (!getIsFeatureToggleEnabled('orchestratorProbeApi')) {
      markStart('COMPLETE_IDENTITY_VALIDATION_LEGACY');
      return completeIdentityValidationLegacy(event);
    }

    markStart('COMPLETE_IDENTITY_VALIDATION');
    const stime = Date.now();
    const accessToken = getAccessToken()?.token ?? '';
    const idValidationSessionId = getIdValidationSessionId() ?? '';

    pushCompleteIdentityValidationRequest();

    const probeResponse = await fetchOrchProbe({
      accessToken,
      apikey: getApiKey(),
      idValidationSessionId,
      isVdiDisabled: getIsVdiDisabled(),
      otpCode: event.data.validationData
    });

    pushCompleteIdentityValidationResponse(probeResponse.ok, Date.now() - stime);

    if (!probeResponse.ok) {
      if (getOrchestratorLockoutFlow(probeResponse)) {
        //clear access token for lock out cases only
        saveUser({
          accessToken: null
        });

        markEnd('COMPLETE_IDENTITY_VALIDATION', {
          reason: 'ACCT_INACCESSIBLE',
          doFlush: true,
          failure: true
        });

        saveUser({
          isLocked: true
        });

        return error('ACCT_INACCESSIBLE');
      }

      const OTPValidationError = getOrchestratorOTPErrors(probeResponse);

      if (OTPValidationError) {
        markEnd('COMPLETE_IDENTITY_VALIDATION', {
          reason: OTPValidationError,
          doFlush: true,
          failure: true
        });

        // START GENAI@GHCOPILOT
        pushErrors('COMPLETE_IDENTITY_VALIDATION', OTPValidationError);
        // END GENAI@GHCOPILOT

        return error(OTPValidationError);
      }

      markEnd('COMPLETE_IDENTITY_VALIDATION', {
        reason: 'UNKNOWN_ERROR',
        doFlush: true,
        failure: true
      });

      return error('UNKNOWN_ERROR');
    }

    const tncEntryInfo = probeResponse.data.customProbeData?.tncEntryInfo;
    const newAccessToken = getHeader(probeResponse, 'access_token') as string;
    const expiresIn = parseExpiryFromAccessToken(newAccessToken) as number;

    const userProfileUpdates: UserProfile = {
      accessToken: {
        expiresIn,
        isPartial: false,
        token: newAccessToken
      },
      isLoggedInOtpUser: true
    };

    if (tncEntryInfo) {
      userProfileUpdates.tnc = {
        status: 'not_accepted',
        tncEntryInfo
      };
    }

    const rememberMeUserName = getUsernameFromRememberMe();

    // When isRecognized is true but user does otp login with a different email,
    // revoke recognized tokens and clear erm/ssi cookies
    if (rememberMeUserName) {
      await unbind({
        accessToken: newAccessToken,
        ignoreAccessToken: true,
        marks: {
          collection: 'complete_identity_validation_revoke_all_tokens',
          revoke: 'complete_identity_validation_revoke_token_erm'
        }
      });

      userProfileUpdates.isRecognizedUser = false;
    }

    const idToken = probeResponse.data.idToken;

    if (idToken) {
      userProfileUpdates.idToken = idToken;
    }

    saveUser(userProfileUpdates);

    const payload: CompleteIdentityValidationPayload = {
      idToken
    };

    markEnd('COMPLETE_IDENTITY_VALIDATION', { doFlush: true });
    return payload;
  } catch (err: unknown) {
    // START GENAI@GHCOPILOT
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    const sErr = JSON.stringify(err) !== undefined ? JSON.stringify(err) : 'Unknown error';
    markEnd('COMPLETE_IDENTITY_VALIDATION', {
      doFlush: true,
      failure: true,
      reason: sErr
    });

    pushErrors('COMPLETE_IDENTITY_VALIDATION', sErr);

    return error('COMPLETE_IDENTITY_VALIDATION_FAILED', sErr);
    // END GENAI@GHCOPILOT
  }
}
